import type { Application, Endpoints } from "../api";
import { applicationTypeLabel, formatDate } from "../presentation";
import { ManagementHeading } from "./ManagementCommon";
import { Button } from "./ui/Button";
import { CopyField } from "./ui/CopyField";
import { Icon } from "./ui/Icon";

/** 只展示应用基础配置；密钥和 UserSig 等安全字段由凭证页面独立管理。 */
export function ApplicationSettingsView({ application, endpoints, applications, busy, onSelect, onEdit, onToggle }: {
  readonly application: Application;
  readonly endpoints: Endpoints | null;
  readonly applications: readonly Application[];
  readonly busy: boolean;
  readonly onSelect: (application: Application) => void;
  readonly onEdit: () => void;
  readonly onToggle: () => void;
}) {
  const native = application.appType === "native";
  return <div className="management-page application-settings-page">
    <ManagementHeading title="基础配置" description="管理应用名称、平台类型和客户端接入边界。" applications={applications} selected={application} onSelect={onSelect} action={<Button variant="primary" onClick={onEdit}>编辑配置</Button>} />
    <div className="settings-layout application-settings-layout">
      <section className="surface-card settings-panel">
        <header><span><Icon name={native ? "smartphone" : "globe"} /></span><div><h3>应用信息</h3><p>{applicationTypeLabel(application.appType)} · 创建后类型不可修改</p></div></header>
        <dl className="configuration-list">
          <div><dt>应用名称</dt><dd>{application.name}</dd></div>
          <div><dt>应用描述</dt><dd>{application.description ?? "未填写"}</dd></div>
          <div><dt>应用状态</dt><dd>{application.status}</dd></div>
          <div><dt>配置版本</dt><dd>v{application.version}</dd></div>
          <div><dt>最后更新</dt><dd>{formatDate(application.updatedAt)}</dd></div>
        </dl>
      </section>
      <aside className="surface-card settings-panel endpoint-panel">
        <header><span><Icon name="activity" /></span><div><h3>公共端点</h3><p>客户端和业务服务端使用的生产地址</p></div></header>
        <div className="copy-field-grid"><CopyField label="REST API" value={endpoints?.api ?? "加载中…"} /><CopyField label="WebSocket" value={endpoints?.webSocket ?? "加载中…"} /></div>
      </aside>
    </div>
    <section className="data-card platform-boundary-card">
      <header className="data-card-header"><div><h3>{native ? "原生平台配置" : "Web 安全边界"}</h3><p>{native ? "当前应用通过 AppKey + UserSig 接入" : "请求来源必须与以下白名单精确匹配"}</p></div></header>
      {native ? <div className="capability-row"><span className="capability-icon"><Icon name="smartphone" /></span><div><strong>iOS、Android、Flutter 与桌面端</strong><p>客户端共享同一个 AppKey，业务服务端负责签发用户 UserSig。</p></div><span className="management-tag tag-success">已支持</span></div> : <div className="uri-settings-grid"><UriSettings title="Redirect URI" values={application.redirectUris} /><UriSettings title="Allowed Origin" values={application.allowedOrigins} /></div>}
    </section>
    <section className="surface-card danger-zone application-state-zone"><div><span><Icon name={application.enabled ? "warning" : "activity"} /></span><div><h3>{application.enabled ? "停用应用" : "启用应用"}</h3><p>{application.enabled ? "停用后将拒绝签发新的登录票据，已有客户端可能在票据失效后离线。" : "启用后恢复新票据签发和应用访问。"}</p></div></div><Button loading={busy} variant={application.enabled ? "danger" : "primary"} onClick={onToggle}>{application.enabled ? "停用应用" : "启用应用"}</Button></section>
  </div>;
}

function UriSettings({ title, values }: { readonly title: string; readonly values: readonly string[] }) {
  return <div><h4>{title}</h4>{values.length === 0 ? <p className="muted-value">未配置</p> : values.map((value) => <code key={value}>{value}</code>)}</div>;
}
